// Minimal toast notifications. Pull via context.

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

type ToastKind = 'success' | 'error' | 'info';
type Toast = { id: number; kind: ToastKind; message: string };
const ctx = createContext<{ push: (kind: ToastKind, message: string) => void } | null>(null);

// Module-level bridge so code outside the React tree (the global react-query MutationCache
// error handler in main.tsx) can raise a toast. Set by the mounted ToastProvider.
let globalPush: ((kind: ToastKind, message: string) => void) | null = null;
export function toastGlobal(kind: ToastKind, message: string) {
  if (globalPush) globalPush(kind, message);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((kind: ToastKind, message: string) => {
    const id = Date.now() + Math.random();
    setToasts(p => [...p, { id, kind, message }]);
    // Errors stay up longer: they usually need reading.
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), kind === 'error' ? 8000 : 4000);
  }, []);
  useEffect(() => {
    globalPush = push;
    return () => { if (globalPush === push) globalPush = null; };
  }, [push]);
  return (
    <ctx.Provider value={{ push }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[60] space-y-2" role="status" aria-live="polite">
        {toasts.map(t => {
          const Icon = t.kind === 'success' ? CheckCircle : t.kind === 'error' ? AlertCircle : Info;
          const color = t.kind === 'success' ? 'border-success text-success' : t.kind === 'error' ? 'border-danger text-danger' : 'border-primary text-primary';
          return (
            <div key={t.id} className={`bg-surface border ${color} rounded-md p-3 shadow-lg flex items-start gap-2 max-w-sm`}>
              <Icon size={16} className="mt-0.5 flex-shrink-0" />
              <div className="text-sm text-textPrimary flex-1">{t.message}</div>
              <button onClick={() => setToasts(p => p.filter(x => x.id !== t.id))}><X size={14} className="text-textSecondary hover:text-textPrimary" /></button>
            </div>
          );
        })}
      </div>
    </ctx.Provider>
  );
}

export function useToast() {
  const v = useContext(ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v.push;
}
